import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const DownloadPanel = ({ 
  qrCodeUrl, 
  restaurantName,
  restaurantProfile,
  qrSettings,
  onDownload,
  onTest 
}) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatingPreset, setGeneratingPreset] = useState(null);

  const printPresets = [
    { name: 'Business Card', label: 'Carte de visite', size: '3.5" × 2"' },
    { name: 'Table Tent', label: 'Chevalet de table', size: '4" × 4"' },
    { name: 'Poster', label: 'Affiche', size: '8.5" × 14"' },
  ];

  // Map color setting to RGB values
  const getColorRGB = (colorSetting) => {
    switch (colorSetting) {
      case 'secondary': return [71, 85, 105]; // Slate Gray
      case 'accent': return [234, 88, 12];    // Coral Orange
      case 'black': return [0, 0, 0];          // Classic Black
      case 'primary':
      default: return [74, 50, 30];            // Primary Brown
    }
  };

  const getImageDataUrl = (url) =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 800;
        canvas.height = img.naturalHeight || 800;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

  // Helper to sanitize text for jsPDF — keeps Latin-1 accents (é è ê à ç ô û etc.)
  // Only replaces characters that fall outside the Latin-1 range supported by helvetica
  const sanitizeForPDF = (text) => {
    if (!text) return '';
    return text
      ?.replace(/[ß]/g, 'ss')
      ?.replace(/[«»\u201C\u201D\u201E\u201F]/g, '"')
      ?.replace(/[''`\u2018\u2019\u201A\u201B]/g, "'")
      ?.replace(/[–—\u2013\u2014]/g, '-')
      ?.replace(/[\u2026]/g, '...')
      ?.replace(/[\u00A0]/g, ' ')
      ?.replace(/[^\x00-\xFF]/g, '');
  };

  // Business Card: 3.5" x 2" (88.9mm x 50.8mm)
  // Restaurant info on left, QR code on right
  const handleBusinessCardPDF = async () => {
    if (!qrCodeUrl) return;
    setGeneratingPreset('Business Card');
    setIsGeneratingPDF(true);
    try {
      const { jsPDF } = await import('jspdf');
      const wMm = 88.9;
      const hMm = 50.8;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [wMm, hMm] });

      const [r, g, b] = getColorRGB(qrSettings?.color);
      const includeLogo = qrSettings?.includeLogo !== false;
      const addBorder = qrSettings?.addBorder !== false;
      const promoMessage = qrSettings?.promoMessage || '';
      const expirationDate = qrSettings?.expirationDate || '';

      // Background
      doc?.setFillColor(252, 249, 245);
      doc?.rect(0, 0, wMm, hMm, 'F');

      // Left accent bar using selected color
      doc?.setFillColor(r, g, b);
      doc?.rect(0, 0, 2.5, hMm, 'F');

      // Right section background for QR
      doc?.setFillColor(245, 240, 233);
      doc?.rect(wMm * 0.58, 0, wMm * 0.42, hMm, 'F');

      // Divider line
      doc?.setDrawColor(200, 180, 160);
      doc?.setLineWidth(0.3);
      doc?.line(wMm * 0.58, 4, wMm * 0.58, hMm - 4);

      // Optional border around the card
      if (addBorder) {
        doc?.setDrawColor(r, g, b);
        doc?.setLineWidth(0.5);
        doc?.rect(0.5, 0.5, wMm - 1, hMm - 1);
      }

      const name = restaurantProfile?.name || qrSettings?.restaurantName || restaurantName || 'Restaurant';
      const tagline = restaurantProfile?.tagline || '';
      const phone = restaurantProfile?.phone || '';
      const email = restaurantProfile?.email || '';
      const address = restaurantProfile?.address || '';

      // Left side — restaurant info
      const leftX = 5.5;
      let curY = 10;

      // Restaurant name using selected color
      doc?.setFont('helvetica', 'bold');
      doc?.setFontSize(9);
      doc?.setTextColor(r, g, b);
      doc?.text(name, leftX, curY, { maxWidth: wMm * 0.52 });
      curY += 5;

      // Tagline
      if (tagline) {
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(6);
        doc?.setTextColor(130, 100, 70);
        doc?.text(tagline, leftX, curY, { maxWidth: wMm * 0.52 });
        curY += 5;
      }

      // Separator using selected color
      doc?.setDrawColor(r, g, b);
      doc?.setLineWidth(0.3);
      doc?.line(leftX, curY, wMm * 0.54, curY);
      curY += 4;

      // Contact info
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(6);
      doc?.setTextColor(80, 60, 40);

      if (phone) {
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(5.5);
        doc?.setTextColor(r, g, b);
        doc?.text('TÉL', leftX, curY);
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(60, 45, 30);
        doc?.text(phone, leftX + 8, curY);
        curY += 4;
      }
      if (email) {
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(5.5);
        doc?.setTextColor(r, g, b);
        doc?.text('EMAIL', leftX, curY);
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(60, 45, 30);
        doc?.text(email, leftX + 8, curY, { maxWidth: wMm * 0.48 });
        curY += 4;
      }
      if (address) {
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(5.5);
        doc?.setTextColor(r, g, b);
        doc?.text('ADRESSE', leftX, curY);
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(60, 45, 30);
        doc?.text(address, leftX + 8, curY, { maxWidth: wMm * 0.46 });
        curY += 4;
      }

      // Promo message
      if (promoMessage) {
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(5);
        doc?.setTextColor(r, g, b);
        doc?.text(`${sanitizeForPDF(promoMessage)}`, leftX, curY, { maxWidth: wMm * 0.52 });
        curY += 3.5;
      }

      // Expiration date
      if (expirationDate) {
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(4.5);
        doc?.setTextColor(140, 110, 80);
        const expFormatted = new Date(expirationDate)?.toLocaleDateString('fr-FR');
        doc?.text(`Valide jusqu'au ${expFormatted}`, leftX, curY, { maxWidth: wMm * 0.52 });
      }

      // Right side — QR code
      const qrAreaX = wMm * 0.58;
      const qrAreaW = wMm * 0.42;
      const qrPad = 4;
      const qrSize = Math.min(qrAreaW, hMm) - qrPad * 2;
      const qrX = qrAreaX + (qrAreaW - qrSize) / 2;
      const qrY = (hMm - qrSize) / 2;

      const dataUrl = await getImageDataUrl(qrCodeUrl);
      if (dataUrl) {
        doc?.addImage(dataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      }

      // Logo overlay on QR removed

      // "Scan to view menu" label using selected color
      doc?.setFont('helvetica', 'italic');
      doc?.setFontSize(4.5);
      doc?.setTextColor(r, g, b);
      doc?.text('Scannez pour voir le menu', qrAreaX + qrAreaW / 2, hMm - 2.5, { align: 'center' });

      doc?.save(`${name?.replace(/\s+/g, '_')}_BusinessCard.pdf`);
    } catch (err) {
      console.error('Business card PDF error:', err);
    } finally {
      setIsGeneratingPDF(false);
      setGeneratingPreset(null);
    }
  };

  // Table Tent: 4" x 4" (101.6mm x 101.6mm) — QR fills the entire square
  const handleTableTentPDF = async () => {
    if (!qrCodeUrl) return;
    setGeneratingPreset('Table Tent');
    setIsGeneratingPDF(true);
    try {
      const { jsPDF } = await import('jspdf');
      const sizeMm = 101.6; // 4 inches
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [sizeMm, sizeMm] });

      // White background
      doc?.setFillColor(255, 255, 255);
      doc?.rect(0, 0, sizeMm, sizeMm, 'F');

      const dataUrl = await getImageDataUrl(qrCodeUrl);
      if (dataUrl) {
        // QR fills the entire square with no padding
        doc?.addImage(dataUrl, 'PNG', 0, 0, sizeMm, sizeMm);
      } else {
        doc?.setFillColor(240, 240, 240);
        doc?.rect(0, 0, sizeMm, sizeMm, 'F');
        doc?.setFont('helvetica', 'normal');
        doc?.setFontSize(12);
        doc?.setTextColor(100, 100, 100);
        doc?.text('QR Code', sizeMm / 2, sizeMm / 2, { align: 'center' });
      }

      const name = restaurantProfile?.name || qrSettings?.restaurantName || restaurantName || 'Restaurant';
      doc?.save(`${name?.replace(/\s+/g, '_')}_TableTent.pdf`);
    } catch (err) {
      console.error('Table tent PDF error:', err);
    } finally {
      setIsGeneratingPDF(false);
      setGeneratingPreset(null);
    }
  };

  // Poster: 8.5" x 14" (215.9mm x 355.6mm) — restaurant title, logo, QR code
  const handlePosterPDF = async () => {
    if (!qrCodeUrl) return;
    setGeneratingPreset('Poster');
    setIsGeneratingPDF(true);
    try {
      const { jsPDF } = await import('jspdf');
      const wMm = 215.9;
      const hMm = 355.6;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [wMm, hMm] });

      const [r, g, b] = getColorRGB(qrSettings?.color);
      const includeLogo = qrSettings?.includeLogo !== false;
      const addBorder = qrSettings?.addBorder !== false;
      const promoMessage = qrSettings?.promoMessage || '';
      const expirationDate = qrSettings?.expirationDate || '';

      const margin = 18;
      const contentW = wMm - margin * 2;

      // Background
      doc?.setFillColor(252, 249, 245);
      doc?.rect(0, 0, wMm, hMm, 'F');

      // Top decorative band using selected color
      doc?.setFillColor(r, g, b);
      doc?.rect(0, 0, wMm, 14, 'F');

      // Bottom decorative band using selected color
      doc?.setFillColor(r, g, b);
      doc?.rect(0, hMm - 14, wMm, 14, 'F');

      // Optional border
      if (addBorder) {
        doc?.setDrawColor(r, g, b);
        doc?.setLineWidth(1);
        doc?.rect(5, 5, wMm - 10, hMm - 10);
      }

      const name = restaurantProfile?.name || qrSettings?.restaurantName || restaurantName || 'Restaurant';
      const tagline = restaurantProfile?.tagline || '';
      const phone = restaurantProfile?.phone || '';
      const email = restaurantProfile?.email || '';
      const address = restaurantProfile?.address || '';
      const logoUrl = restaurantProfile?.logo || null;

      // ── TOP SECTION: Logo + Restaurant Name side by side ──────────────────
      // Start lower so content doesn't overlap the top border band (band ends at y=14)
      let curY = 32;
      const logoSize = 22;

      // Pre-load logo if needed
      let logoDataUrl = null;
      if (includeLogo && logoUrl) {
        logoDataUrl = await getImageDataUrl(logoUrl);
      }

      // Measure name text width to center the logo+name group
      doc?.setFont('helvetica', 'bold');
      doc?.setFontSize(28);
      const nameTextWidth = doc?.getTextWidth(name);
      const logoGap = 4; // gap between logo and name
      const groupW = includeLogo && logoDataUrl ? logoSize + logoGap + nameTextWidth : nameTextWidth;
      const groupX = (wMm - groupW) / 2;

      if (includeLogo && logoDataUrl) {
        const logoY = curY - logoSize * 0.75; // vertically align logo center with text baseline
        doc?.addImage(logoDataUrl, 'PNG', groupX, logoY, logoSize, logoSize);
        // Restaurant name to the right of logo
        doc?.setTextColor(r, g, b);
        doc?.text(name, groupX + logoSize + logoGap, curY, { baseline: 'alphabetic' });
      } else {
        // No logo — just center the name
        doc?.setTextColor(r, g, b);
        doc?.text(name, wMm / 2, curY, { align: 'center', maxWidth: contentW });
      }
      curY += 8;

      // Tagline
      if (tagline) {
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(13);
        doc?.setTextColor(140, 100, 65);
        doc?.text(sanitizeForPDF(tagline), wMm / 2, curY, { align: 'center', maxWidth: contentW });
        curY += 9;
      }

      // Decorative line
      doc?.setDrawColor(r, g, b);
      doc?.setLineWidth(0.8);
      doc?.line(margin + 20, curY, wMm - margin - 20, curY);
      curY += 10;

      // ── MIDDLE SECTION: QR Code centered ──────────────────────────────────
      // Reserve space for bottom info to calculate QR position
      // Estimate bottom block height
      let bottomBlockH = 0;
      if (phone) bottomBlockH += 7;
      if (email) bottomBlockH += 7;
      if (address) bottomBlockH += 10;
      if (promoMessage) bottomBlockH += 18;
      if (expirationDate) bottomBlockH += 10;
      bottomBlockH += 30; // scan label + instruction + url + padding

      const availableH = hMm - 14 - curY - bottomBlockH;
      const qrSize = Math.min(contentW * 0.72, availableH - 10);
      const qrX = (wMm - qrSize) / 2;
      const qrY = curY + (availableH - qrSize) / 2;

      // "Scan to view our menu" label — above QR
      const labelY = qrY - 12;
      doc?.setFont('helvetica', 'bold');
      doc?.setFontSize(15);
      doc?.setTextColor(r, g, b);
      doc?.text('SCANNEZ POUR VOIR NOTRE MENU', wMm / 2, labelY, { align: 'center' });

      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(10);
      doc?.setTextColor(140, 110, 80);
      doc?.text('Pointez votre camera sur le code QR ci-dessous', wMm / 2, labelY + 7, { align: 'center' });

      // QR code
      const dataUrl = await getImageDataUrl(qrCodeUrl);
      if (dataUrl) {
        doc?.addImage(dataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      } else {
        doc?.setFillColor(240, 235, 228);
        doc?.rect(qrX, qrY, qrSize, qrSize, 'F');
        doc?.setFontSize(12);
        doc?.setTextColor(120, 100, 80);
        doc?.text('QR Code', wMm / 2, qrY + qrSize / 2, { align: 'center' });
      }

      // Logo overlay on QR removed

      // ── BOTTOM SECTION: Info below QR ─────────────────────────────────────
      curY = qrY + qrSize + 8;

      // Decorative line before info
      doc?.setDrawColor(r, g, b);
      doc?.setLineWidth(0.5);
      doc?.line(margin + 20, curY, wMm - margin - 20, curY);
      curY += 8;

      // Contact info block
      if (phone || email || address) {
        doc?.setFont('helvetica', 'normal');
        doc?.setFontSize(10);
        doc?.setTextColor(80, 60, 40);
        if (phone) {
          doc?.setFont('helvetica', 'bold');
          doc?.setTextColor(r, g, b);
          doc?.text('TEL: ', wMm / 2 - 30, curY);
          doc?.setFont('helvetica', 'normal');
          doc?.setTextColor(80, 60, 40);
          doc?.text(sanitizeForPDF(phone), wMm / 2 - 15, curY);
          curY += 7;
        }
        if (email) {
          doc?.setFont('helvetica', 'bold');
          doc?.setTextColor(r, g, b);
          doc?.text('EMAIL: ', wMm / 2 - 30, curY);
          doc?.setFont('helvetica', 'normal');
          doc?.setTextColor(80, 60, 40);
          doc?.text(sanitizeForPDF(email), wMm / 2 - 8, curY, { maxWidth: contentW * 0.6 });
          curY += 7;
        }
        if (address) {
          doc?.setFont('helvetica', 'bold');
          doc?.setTextColor(r, g, b);
          doc?.text('ADRESSE: ', wMm / 2 - 30, curY);
          doc?.setFont('helvetica', 'normal');
          doc?.setTextColor(80, 60, 40);
          doc?.text(sanitizeForPDF(address), wMm / 2 - 2, curY, { maxWidth: contentW * 0.55 });
          curY += 10;
        }
      }

      // Promo message
      if (promoMessage) {
        doc?.setFillColor(r, g, b);
        doc?.roundedRect(margin + 10, curY - 5, contentW - 20, 12, 3, 3, 'F');
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(11);
        doc?.setTextColor(255, 255, 255);
        doc?.text(`${sanitizeForPDF(promoMessage)}`, wMm / 2, curY + 3, { align: 'center', maxWidth: contentW - 30 });
        curY += 18;
      }

      // Expiration date
      if (expirationDate) {
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(9);
        doc?.setTextColor(140, 110, 80);
        const expFormatted = new Date(expirationDate)?.toLocaleDateString('fr-FR');
        doc?.text(`Valide jusqu'au ${expFormatted}`, wMm / 2, curY, { align: 'center' });
        curY += 10;
      }

      // URL below info
      const menuUrl = qrSettings?.menuUrl || '';
      if (menuUrl) {
        doc?.setFont('helvetica', 'normal');
        doc?.setFontSize(8);
        doc?.setTextColor(160, 130, 100);
        doc?.text(menuUrl, wMm / 2, curY, { align: 'center', maxWidth: contentW });
      }

      // Footer text on dark band
      doc?.setFont('helvetica', 'italic');
      doc?.setFontSize(9);
      doc?.setTextColor(220, 200, 175);
      doc?.text(sanitizeForPDF(name), wMm / 2, hMm - 5.5, { align: 'center' });

      doc?.save(`${name?.replace(/\s+/g, '_')}_Poster.pdf`);
    } catch (err) {
      console.error('Poster PDF error:', err);
    } finally {
      setIsGeneratingPDF(false);
      setGeneratingPreset(null);
    }
  };

  const handlePrintPresetPDF = (preset) => {
    if (preset?.name === 'Business Card') return handleBusinessCardPDF();
    if (preset?.name === 'Table Tent') return handleTableTentPDF();
    if (preset?.name === 'Poster') return handlePosterPDF();
  };

  const handlePDFDownload = async () => {
    if (!qrCodeUrl) return;
    setIsGeneratingPDF(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageW = doc?.internal?.pageSize?.getWidth();
      const pageH = doc?.internal?.pageSize?.getHeight();
      const margin = 20;
      const contentW = pageW - margin * 2;

      doc?.setFillColor(245, 240, 235);
      doc?.rect(0, 0, pageW, 42, 'F');

      doc?.setFont('helvetica', 'bold');
      doc?.setFontSize(22);
      doc?.setTextColor(74, 50, 30);
      const name = qrSettings?.restaurantName || restaurantName || 'Restaurant';
      doc?.text(name, pageW / 2, 18, { align: 'center' });

      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(11);
      doc?.setTextColor(120, 100, 80);
      doc?.text('QR Code — Menu Digital', pageW / 2, 28, { align: 'center' });

      doc?.setDrawColor(200, 180, 160);
      doc?.setLineWidth(0.4);
      doc?.line(margin, 36, pageW - margin, 36);

      const qrSize = 80;
      const qrX = (pageW - qrSize) / 2;
      const qrY = 46;

      const dataUrl = await getImageDataUrl(qrCodeUrl);
      if (dataUrl) {
        doc?.addImage(dataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      } else {
        doc?.setDrawColor(180, 160, 140);
        doc?.setFillColor(250, 248, 245);
        doc?.rect(qrX, qrY, qrSize, qrSize, 'FD');
        doc?.setFontSize(9);
        doc?.setTextColor(150, 130, 110);
        doc?.text('QR Code', pageW / 2, qrY + qrSize / 2, { align: 'center' });
      }

      const urlY = qrY + qrSize + 8;
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(8);
      doc?.setTextColor(100, 90, 80);
      const url = qrSettings?.menuUrl || '';
      doc?.text(url, pageW / 2, urlY, { align: 'center', maxWidth: contentW });

      doc?.setFillColor(245, 240, 235);
      doc?.rect(0, pageH - 18, pageW, 18, 'F');
      doc?.setFont('helvetica', 'italic');
      doc?.setFontSize(8);
      doc?.setTextColor(140, 120, 100);
      const today = new Date()?.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      doc?.text(`Généré le ${today}`, margin, pageH - 8);
      doc?.text('QR Menu AR', pageW - margin, pageH - 8, { align: 'right' });

      doc?.save(`${(qrSettings?.restaurantName || 'qr-code')?.replace(/\s+/g, '_')}_QR.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadFormat = (format) => {
    if (format === 'pdf') {
      handlePDFDownload();
    } else {
      onDownload?.(format);
    }
  };

  return (
    <div className="download-panel bg-card rounded-xl p-6 md:p-8 shadow-warm-lg transition-smooth">
      <div className="panel-header mb-6 md:mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="bg-success/10 p-2 rounded-lg">
            <Icon name="Download" size={24} color="var(--color-success)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            Télécharger & Déployer
          </h2>
        </div>
        <p className="text-sm md:text-base text-muted-foreground font-caption">
          Exportez votre code QR en plusieurs formats
        </p>
      </div>
      <div className="print-presets mb-8">
        <h3 className="text-base md:text-lg font-heading font-medium text-foreground mb-1">
          Formats d'impression
        </h3>
        <p className="text-xs text-muted-foreground font-caption mb-4">
          Télécharge un PDF prêt pour Adobe Acrobat
        </p>
        <div className="grid grid-cols-3 gap-3">
          {printPresets?.map((preset) => {
            const isLoading = isGeneratingPDF && generatingPreset === preset?.name;
            return (
              <button
                key={preset?.name}
                onClick={() => handlePrintPresetPDF(preset)}
                disabled={!qrCodeUrl || isGeneratingPDF}
                className="preset-button bg-muted/50 hover:bg-muted rounded-lg p-3 md:p-4 border border-border hover:border-accent/50 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed touch-target"
              >
                <div className="text-center">
                  <Icon
                    name={isLoading ? 'Loader' : 'Printer'}
                    size={20}
                    className={`mx-auto mb-2 ${isLoading ? 'text-primary animate-spin' : 'text-accent'}`}
                  />
                  <p className="text-xs md:text-sm font-heading font-medium text-foreground mb-1">
                    {preset?.label}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">
                    {preset?.size}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DownloadPanel;